import { AppResponse } from 'modules/core/interfaces/appResponse';
import axios, { AxiosInstance } from 'axios';
import environment from '@/environment';
import store from '@/store';
import router from '@/router';
import AppMessage from 'modules/core/models/appMessage';
import i18n from '@/i18n/index';

type CallbackFunction = (data?: any) => Promise<any>;
interface AxiosAuthorizedApi extends AxiosInstance {
  simpleGet: (
    apiEndpoint: string,
    callbackSuccess: CallbackFunction,
    callbackError: CallbackFunction,
  ) => void;
  simplePost: (
    apiEndpoint: string,
    postData: any,
    callbackSuccess: CallbackFunction,
    callbackError: CallbackFunction,
  ) => void;
}

const AuthorizedApiClient: AxiosAuthorizedApi = Object.assign(
  axios.create({
    baseURL: environment.backendUrl + environment.apiPrefix,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  {
    simplePost(
      apiEndpoint: string,
      postData: any,
      callbackSuccess: CallbackFunction,
      callbackError: CallbackFunction,
    ) {
      AuthorizedApiClient.post(apiEndpoint, postData)
        .then((response: any) => {
          if (response.status === 200) {
            const responseData = response.data;

            if (responseData.success) {
              callbackSuccess(responseData.data);
            } else {
              callbackError(responseData.message);
            }
          } else {
            callbackError();
          }
        })
        .catch((error: Error) => {
          callbackError(error);
        });
    },

    simpleGet(
      apiEndpoint: string,
      callbackSuccess: CallbackFunction,
      callbackError: CallbackFunction,
    ) {
      AuthorizedApiClient.get(apiEndpoint)
        .then((response: any) => {
          if (response.status === 200) {
            const responseData = response.data;

            if (responseData.success) {
              callbackSuccess(responseData.data);
            } else {
              callbackError(responseData.message);
            }
          } else {
            callbackError();
          }
        })
        .catch((error: Error) => {
          callbackError(error);
        });
    },
  },
);

AuthorizedApiClient.interceptors.request.use(
  (config: any) => {
    let user = null;
    if (store.state.auth && store.state.auth.user) {
      user = store.state.auth.user;
    }

    if (user && user.access_token) {
      config.headers = Object.assign(config.headers, {
        Authorization: 'Bearer ' + user.access_token,
      });
    }

    if (user && user.language) {
      let language = 'pl-PL';
      if(user.language == 'en') {
        language = 'en-US';
      }
      config.headers = Object.assign(config.headers, {
        "Accept-Language": language,
      });
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error);
  },
);

AuthorizedApiClient.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    console.error(error.response.data);

    if (error.response && error.response.data) {
      const appResponse: AppResponse<any> = error.response.data;
      const errorMsgs = [];

      for (const errorCode of appResponse.ErrorCodes) {
        errorMsgs.push(i18n.t(`backend.errors.${errorCode}`));
      }
      if(errorMsgs.some(x => x === 'backend.errors.49902'))
      {
        //Zrobione tak bo ten błąd w czasie testów będzie pewnie często występował, a nie ma tu żadego serwisu 
        //odpowiedzialnego za tłumaczenie błędów z backend 
        AppMessage.show(i18n.t('wizard.erecruiterAccountWithCompanyIdAlreadyExists'), 'error');
      }
      else if(errorMsgs.some(x => x === 'backend.errors.49903'))
      {
        AppMessage.show(i18n.t('wizard.erecruiterInvalidAccessKey'), 'error');
      }
      else if(errorMsgs.some(x => x === 'backend.errors.49904'))
      {
        AppMessage.show(i18n.t('wizard.apiloInvalidCredentials'), 'error');
      }
      else{
      AppMessage.show(errorMsgs.join('<br>'), 'error');
      }
    }

    if (error.response && error.response.status === 401) {
      store.commit('auth/logout');
      router.push('/login');
    }
  },
);

export default AuthorizedApiClient;
